/*
 * Postgres 컬럼이 snake_case 라 행 → 도메인 변환 지점에서만 규칙을 끈다
 * (변환 이후 도메인 타입은 전부 camelCase — matching.api 와 같은 이유·같은 방식).
 */
/* eslint-disable @typescript-eslint/naming-convention */
import { getAuthedClient, supabase } from '~/common/utils/supabase.util.ts';
import { getMyProfileId } from '~/features/profile/api/profile.api';

import type { Review, ReviewInput } from '~/features/review/types/review.type';

/**
 * 후기 어댑터 — `reviews` 테이블.
 *
 * @description 조회는 **로그인 없이** 한다(지도사 상세는 둘러보기 화면이다).
 *
 *  작성 자격은 앱이 아니라 RLS 가 판정한다 — `completed` 상태이고 본인이 그 신청의 보호자일 때만
 *  통과한다. 그래서 앱은 "쓸 수 있는지"를 다시 검사하지 않고, 버튼을 보여줄지만 결정한다.
 */

interface ReviewRow {
  id: string;
  request_id: string;
  guardian_id: string;
  tutor_id: string;
  class_id: string;
  rating: number;
  body: string | null;
  created_at: string;
  guardian: { nickname: string } | null;
}

const REVIEW_COLUMNS =
  'id, request_id, guardian_id, tutor_id, class_id, rating, body, created_at, guardian:profiles!reviews_guardian_id_fkey(nickname)';

function toReview(row: ReviewRow): Review {
  return {
    id: row.id,
    requestId: row.request_id,
    guardianId: row.guardian_id,
    guardianName: row.guardian?.nickname ?? '',
    tutorId: row.tutor_id,
    classId: row.class_id,
    rating: row.rating,
    body: row.body ?? undefined,
    createdAt: row.created_at,
  };
}

/** 지도사 상세의 후기 목록 */
export async function fetchTutorReviewsApi(tutorId: string): Promise<Review[]> {
  if (!supabase) return [];

  const { data, error } = await supabase
    .from('reviews')
    .select(REVIEW_COLUMNS)
    .eq('tutor_id', tutorId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return ((data as unknown as ReviewRow[] | null) ?? []).map(toReview);
}

/**
 * 내가 쓴 후기.
 * @description "이 수업에 이미 후기를 썼는지"를 판정하는 근거다. 후기는 수업 1건당 1개라
 *  이미 쓴 수업에는 작성 버튼 대신 내가 남긴 별점을 보여준다.
 */
export async function fetchMyReviewsApi(): Promise<Review[]> {
  const client = await getAuthedClient();
  if (!client) return [];
  const profileId = await getMyProfileId();
  if (!profileId) return [];

  const { data, error } = await client
    .from('reviews')
    .select(REVIEW_COLUMNS)
    .eq('guardian_id', profileId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return ((data as unknown as ReviewRow[] | null) ?? []).map(toReview);
}

export async function createReviewApi(input: ReviewInput): Promise<Review> {
  const client = await getAuthedClient();
  if (!client) throw new Error('Supabase 세션을 만들 수 없습니다.');
  const profileId = await getMyProfileId();
  if (!profileId) throw new Error('먼저 프로필을 등록해주세요.');

  const { data, error } = await client
    .from('reviews')
    .insert({
      request_id: input.requestId,
      guardian_id: profileId,
      tutor_id: input.tutorId,
      class_id: input.classId,
      rating: input.rating,
      body: input.body?.trim() || null,
    })
    .select(REVIEW_COLUMNS)
    .single();

  if (error) {
    // 같은 수업에 두 번 쓰려 한 경우 — 원인을 그대로 알려야 사용자가 상황을 이해한다.
    if (error.code === '23505') throw new Error('이미 이 수업의 후기를 작성하셨습니다.');
    // RLS 거부 = 완료되지 않은 수업이거나 본인 수업이 아니다.
    if (error.code === '42501') throw new Error('수업이 완료된 후에 후기를 남길 수 있습니다.');
    throw error;
  }
  return toReview(data as unknown as ReviewRow);
}
